import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { PriceModel } from '@app/shared/models/api/price.model';

import { TranslocoModule } from '@ngneat/transloco';
import { InViewportModule } from 'ng-in-viewport';
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { CheckoutOrderSummaryItemComponent } from './checkout-order-summary-item/checkout-order-summary-item.component';
import { DiscountInfo } from '@app/akita/router/models/discount-info.model';
import { AkitaCheckoutQuery } from '@app/akita/api/checkout/state/checkout.query';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AmountPipe } from '@app/shared/pipes/amount.pipe';
import { Insurance } from '@app/akita/api/products/models/insurance.model';
import { CheckoutInstallmentOptionsComponent } from '../checkout-installment-options/checkout-installment-options.component';

@Component({
  selector: 'app-checkout-order-summary',
  templateUrl: './checkout-order-summary.template.html',
  styleUrls: ['./checkout-order-summary.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    InViewportModule,
    // Popsy
    CheckoutOrderSummaryItemComponent,
    AmountPipe,
    CheckoutInstallmentOptionsComponent,
  ],
})
export class CheckoutOrderSummaryComponent implements OnChanges, OnDestroy {
  @Input() public deliveryOrders?: Array<{ productID: string; quantity: number }> | null =
    null;
  @Input() public deliveryProducts?: Array<Product> | null = null;
  @Input() public insurances?: Array<Insurance> | null = null;

  @Input() public discount?: DiscountInfo | null;
  @Input() public productCountry?: string | null;
  @Input() public productCurrency?: string | null;

  @Input() public protectionPrice?: PriceModel | null = null;
  @Input() public isProtected?: boolean | null = null;

  @Input() public deliveryPrice?: PriceModel | null = null;
  @Input() public deliveryFee?: PriceModel | null = null;
  @Input() public total?: PriceModel | null = null;
  @Input() public loading?: boolean | null;
  @Input() public mobileMode?: boolean | null = false;
  @Input() public isCheckout?: boolean | null = true;
  @Input() public compact?: boolean | null = false;
  @Input() public alwaysShowTamaraTabbyPromos?: boolean | null = false;
  @Input() public hideInstallmentOptions?: boolean | null = false;

  public discountCodePrice: WritableSignal<PriceModel | null> = signal(null);
  public deliveryPriceFallback: WritableSignal<PriceModel | null> = signal(null);

  public language: string | null = null;

  public showTabbyPromo$: Observable<boolean | null>;
  public showTamaraPromo$: Observable<boolean | null>;
  public showQuaraPromo$: Observable<boolean | null>;
  public showBaseetaPromo$: Observable<boolean | null>;

  public displayDeliveryPrice: PriceModel | null;
  public displayHandlingFee: PriceModel | null;
  public displayCoDFee: PriceModel | null;
  public displayTotalPrice: PriceModel | null;

  public gotDiscountAfterLocked$: Observable<boolean>;

  public products$: Observable<Array<Product | null>>;
  private readonly productsSubject: BehaviorSubject<Array<Product | null>>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaCheckoutQuery: AkitaCheckoutQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery
  ) {
    this.subscriptions = new Subscription();
    this.destroySubject = new Subject();

    this.language = this.akitaProductsQuery.validateLocale().language;

    this.showTabbyPromo$ = this.akitaCheckoutQuery.selectShowTabbyPromo();
    this.showTamaraPromo$ = this.akitaCheckoutQuery.selectShowTamaraPromo();
    this.showQuaraPromo$ = this.akitaCheckoutQuery.selectShowQuaraPromo();
    this.showBaseetaPromo$ = this.akitaCheckoutQuery.selectShowBaseetaPromo();

    this.gotDiscountAfterLocked$ = this.akitaCheckoutQuery.selectGotDiscountAfterLocked();

    this.displayDeliveryPrice = null;
    this.displayHandlingFee = null;
    this.displayCoDFee = null;
    this.displayTotalPrice = null;
    this.refreshTotalPrice();

    this.productsSubject = new BehaviorSubject([] as any);
    this.products$ = this.productsSubject.asObservable();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.deliveryOrders) {
      this.deliveryOrders?.forEach((item: any) => {
        this.subscriptions.add(
          this.akitaProductsQuery
            .selectProduct(item.productID)
            .pipe(
              tap((product: Product | null) => {
                this.productsSubject.next([...this.productsSubject.value, product]);
              }),
              takeUntil(this.destroySubject)
            )
            .subscribe({ next: () => {}, error: () => {} })
        );
      });
    }
    if (changes.discount) {
      this.discountCodePrice.set(PriceModel.fromJson(this.discount));
    }
    this.gotDiscountAfterLocked$ = this.akitaCheckoutQuery
      .selectGotDiscountAfterLocked()
      .pipe(
        tap((gotDiscount) => {
          if (gotDiscount) {
            const stateDiscount = this.akitaRouterQuery.getDiscountForCurrency(
              this.productCurrency
            );
            if (stateDiscount?.amount) {
              this.discount = {
                amount: stateDiscount?.amount,
                currency: stateDiscount?.currency,
                code: '',
              };
            }
          }
        })
      );

    this.refreshTotalPrice();
  }

  public trackByItem(index: number, item?: any): string {
    return item?.productID || `${index}`;
  }

  public getProductQuantity(id: string): number {
    return this.deliveryOrders?.find((x) => x.productID === `${id}`)?.quantity || 1;
  }

  public getProductInsurance(id: string): Insurance | null {
    return this.insurances?.find((x) => x.productId === id) || null;
  }

  private refreshDeliveryPrice(): void {
    if (this.deliveryPrice) {
      this.displayDeliveryPrice = PriceModel.fromJson(this.deliveryPrice);
    } else {
      this.displayDeliveryPrice = {
        amount: 0,
        currency: this.total?.currency || 'SAR',
      };

      this.deliveryPriceFallback.set({
        amount: 20,
        currency: this.total?.currency || 'SAR',
      });
    }
  }

  private refreshHandlingFees(): void {
    if (this.deliveryFee) {
      this.displayHandlingFee = PriceModel.fromJson(this.deliveryFee);
    } else {
      this.displayHandlingFee = {
        amount: 0,
        currency: this.total?.currency || 'SAR',
      };
    }
  }

  private refreshTotalPrice(stateDiscountAmount?: number | null): void {
    this.refreshDeliveryPrice();
    this.refreshHandlingFees();

    let price = 0;
    if (this.total?.amount) {
      price = price + this.total.amount;
    }

    if (this.displayHandlingFee?.amount) {
      price = price + this.displayHandlingFee.amount;
    }

    if (this.displayDeliveryPrice?.amount) {
      price = price + this.displayDeliveryPrice.amount;
    }

    if (stateDiscountAmount) {
      price = price - stateDiscountAmount;
    }

    this.displayTotalPrice = PriceModel.fromJson({
      amount: price,
      currency: this.total?.currency || 'SAR',
    });

    this.changeDetector.markForCheck();
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
